import React, { useState, useEffect } from "react";
import {
  ShieldAlert,
  AlertTriangle,
  Phone,
  Copy,
  Check,
  RefreshCw,
  LogOut,
  Headphones,
  Monitor,
  Calendar,
} from "lucide-react";
import { api } from "@/lib/api";
import { getDeviceFingerprint } from "../lib/deviceFingerprint";
import { useAuth } from "../context/AuthContext";
import CustomerSupportModal, { SUPPORT_INFO } from "./CustomerSupportModal";
import { toast } from "sonner";

export default function LicenseGate({ children }) {
  const { user, logout } = useAuth();
  const [licenseState, setLicenseState] = useState({
    checking: true,
    valid: true,
    status: "verified",
    message: "",
    registered_machine: "",
    is_grace: false,
    days_left_in_grace: 0,
  });
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const checkLicense = async () => {
    if (!user) {
      setLicenseState((prev) => ({ ...prev, checking: false }));
      return;
    }

    try {
      const fp = await getDeviceFingerprint();
      const res = await api.post("/license/activate", {
        fingerprint_hash: fp.fingerprint_hash,
        machine_name: fp.machine_name,
        os_name: fp.os_name,
        app_version: fp.app_version,
      });

      const data = res.data;
      if (data.valid === false) {
        setLicenseState({
          checking: false,
          valid: false,
          status: data.status,
          message: data.message || "License check failed",
          registered_machine: data.registered_machine || "",
          is_grace: false,
          days_left_in_grace: 0,
        });
      } else {
        setLicenseState({
          checking: false,
          valid: true,
          status: data.status,
          message: "",
          registered_machine: data.machine_name || "",
          is_grace: Boolean(data.grace_period),
          days_left_in_grace: data.days_left_in_grace || 0,
        });
      }
    } catch (err) {
      console.warn("License check network error, operating in offline grace mode:", err);
      setLicenseState({
        checking: false,
        valid: true,
        status: "offline_grace",
        message: "",
        registered_machine: "",
        is_grace: false,
        days_left_in_grace: 0,
      });
    }
  };

  useEffect(() => {
    checkLicense();
  }, [user]);

  const handleCopy = () => {
    navigator.clipboard.writeText(SUPPORT_INFO.phone);
    setCopied(true);
    toast.success("Support phone number copied: " + SUPPORT_INFO.phone);
    setTimeout(() => setCopied(false), 2500);
  };

  // 1. Device Mismatch Screen (1 PC = 1 Active License)
  if (!licenseState.checking && !licenseState.valid && licenseState.status === "device_mismatch") {
    return (
      <div className="fixed inset-0 z-50 bg-zinc-950 flex flex-col items-center justify-center p-6 text-zinc-100 font-sans">
        <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-3xl p-8 shadow-2xl flex flex-col items-center text-center gap-6 animate-scale-in">
          <div className="w-16 h-16 rounded-2xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-400">
            <Monitor className="w-8 h-8" />
          </div>

          <div>
            <div className="text-xs font-bold text-rose-400 uppercase tracking-wider mb-1">
              Device Registration Notice
            </div>
            <h2 className="text-xl font-extrabold text-white">
              Already Active on Another PC
            </h2>
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
              Your PrintQ account is already registered to machine{" "}
              <strong className="text-white font-mono bg-zinc-800 px-1.5 py-0.5 rounded">
                {licenseState.registered_machine || "Another PC"}
              </strong>
              . Under the <strong>1 PC = 1 License</strong> policy, please contact Customer Support to transfer or unbind your PC.
            </p>
          </div>

          {/* Support Highlight Box */}
          <div className="w-full bg-zinc-950 border border-zinc-800/80 rounded-2xl p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-400">Support Executive:</span>
              <strong className="text-white">{SUPPORT_INFO.name}</strong>
            </div>
            <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-xl p-2.5">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-amber-400" />
                <span className="font-mono text-sm font-bold text-white tracking-wide">
                  +91 {SUPPORT_INFO.phone}
                </span>
              </div>
              <button
                onClick={handleCopy}
                className="px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs text-zinc-300 flex items-center gap-1 transition-colors"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? "Copied" : "Copy"}</span>
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 w-full">
            <a
              href={`tel:${SUPPORT_INFO.phone}`}
              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 hover:brightness-110 text-zinc-950 font-bold text-xs shadow-md shadow-amber-500/20 transition-all text-center"
            >
              <Phone className="w-4 h-4" />
              <span>Call Support</span>
            </a>
            <button
              onClick={() => checkLicense()}
              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-semibold text-xs transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Retry Check</span>
            </button>
          </div>

          <button
            onClick={logout}
            className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1.5 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Logout Account</span>
          </button>
        </div>
      </div>
    );
  }

  // 2. Membership Expired Screen (Beyond 7-Day Grace)
  if (!licenseState.checking && !licenseState.valid && licenseState.status === "expired") {
    return (
      <div className="fixed inset-0 z-50 bg-zinc-950 flex flex-col items-center justify-center p-6 text-zinc-100 font-sans">
        <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-3xl p-8 shadow-2xl flex flex-col items-center text-center gap-6 animate-scale-in">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400">
            <Calendar className="w-8 h-8" />
          </div>

          <div>
            <div className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-1">
              Membership Expired
            </div>
            <h2 className="text-xl font-extrabold text-white">
              PrintQ Monthly Subscription Ended
            </h2>
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
              Your monthly membership has expired. To continue using Scan to Print,CamScanner filters, and direct printing, please contact Customer Support to renew for <strong>₹99 / month</strong>.
            </p>
          </div>

          {/* Contact Box */}
          <div className="w-full bg-zinc-950 border border-zinc-800/80 rounded-2xl p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between text-xs">
              <span className="text-zinc-400">Customer Support:</span>
              <strong className="text-white">{SUPPORT_INFO.name}</strong>
            </div>
            <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 rounded-xl p-2.5">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-amber-400" />
                <span className="font-mono text-sm font-bold text-white tracking-wide">
                  +91 {SUPPORT_INFO.phone}
                </span>
              </div>
              <button
                onClick={handleCopy}
                className="px-2.5 py-1 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-xs text-zinc-300 flex items-center gap-1 transition-colors"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? "Copied" : "Copy"}</span>
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 w-full">
            <a
              href={`tel:${SUPPORT_INFO.phone}`}
              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 hover:brightness-110 text-zinc-950 font-bold text-xs shadow-md shadow-amber-500/20 transition-all text-center"
            >
              <Phone className="w-4 h-4" />
              <span>Call to Renew</span>
            </a>
            <button
              onClick={() => checkLicense()}
              className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-semibold text-xs transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Check Status</span>
            </button>
          </div>

          <button
            onClick={logout}
            className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1.5 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Logout Account</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* 3. 7-Day Grace Period Top Notification Banner */}
      {licenseState.is_grace && (
        <div className="bg-amber-500/15 border-b border-amber-500/30 text-amber-200 px-4 py-2 text-xs flex items-center justify-between z-30 shrink-0">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
            <span>
              <strong>Grace Period Active:</strong> Your PrintQ membership has expired. You have <strong>{licenseState.days_left_in_grace} day(s)</strong> of grace period remaining.
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`tel:${SUPPORT_INFO.phone}`}
              className="underline hover:text-white font-bold"
            >
              Renew (Call {SUPPORT_INFO.phone})
            </a>
            <button
              onClick={() => setShowSupportModal(true)}
              className="px-2.5 py-0.5 rounded-md bg-amber-400 text-zinc-950 font-bold text-[11px]"
            >
              Support
            </button>
          </div>
        </div>
      )}

      {children}

      <CustomerSupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />
    </>
  );
}
